<template>
  <div class="map-wrapper">
    <div
      id="map-canvas"
      ref="gmap"
      class="map-canvas"
    />
    <map-info-overlay
      :show="showMarkerOverlay"
      :location="selectedLocation"
      :map-type="options.MapType"
      :cta-button-title="options.CtaButtonTitle"
      :is-mobile="isMobile"
    />
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import { init as gmapsInit } from '@utils/gmaps'
  import TabTrapMixin from '@mixins/tab-trap'
  import MarkerHandler from './mixins/marker-handler'
  import ClusterHandler from './mixins/cluster-handler'
  import MapInteraction from './mixins/map-interaction'
  import MapInfoOverlay from './components/map-info-overlay'

  /**
   * Google Maps component that displays locations with custom markers and clustering.
   * Features:
   * - Custom styled map markers using SVG
   * - Marker clustering for better performance with many locations
   * - Interactive info overlay for selected locations
   * - Mobile-friendly with touch interactions
   * - Smooth zoom and pan animations
   * - Route query parameter integration
   */
  export default {
    name: 'GoogleMap',

    components: {
      MapInfoOverlay
    },

    mixins: [
      TabTrapMixin,
      MarkerHandler,
      ClusterHandler,
      MapInteraction
    ],

    props: {
      properties: {
        type: Array,
        required: true,
        default: () => [],
      },
      options: {
        type: Object,
        required: false,
        default: () => ({
          ApiKey: '',
          Latitude: '48.8668814',
          Longitude: '2.324969',
          ZoomLevel: '2',
          MapType: '',
          CtaButtonTitle: '',
          MapId: 'DEMO_MAP_ID',
          MarkerColor: '#8E6919',
        }),
      },
    },

    data: () => ({
      map: null,
      markers: [],
      isLoaded: false,
      showMarkerOverlay: false,
      geoLocations: [],
      currentMarker: null,
      selectedLocation: {},
    }),

    computed: {
      ...mapState('responsive', ['breakpoints', 'width']),
      isMobile() {
        return this.width <= 1024
      },
    },

    watch: {
      properties(val) {
        this.geoLocations = val
        if (this.map) {
          this.resetZoom()
          this.markers = []
          this.markerClusterAlgorithm = null
          this.markerClusterRenderer = null
          this.markerCluster.clearMarkers()
          this.addMarkers()
          if (this.showMarkerOverlay) {
            this.closeMapContent()
          }
        } else {
          this.mapInit()
        }
      },
      showMarkerOverlay(val) {
        if (this.isMobile) {
          if (val) {
            document.body.classList.add('no-scroll')
          } else {
            document.body.classList.remove('no-scroll')
          }
        } else if (val) {
          document.body.style.overflowX = 'hidden'
        }
      },
    },

    mounted() {
      this.geoLocations = this.properties
      this.mapInit()
    },

    methods: {
      async mapInit() {
        this.isLoaded = false
        try {
          const google = await gmapsInit(this.options.ApiKey)
          const mapOptions = {
            center: new google.maps.LatLng(
              this.options.Latitude,
              this.options.Longitude
            ),
            zoom: Number(this.options.ZoomLevel),
            minZoom: 2,
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            streetViewControl: false,
            fullscreenControl: false,
            mapId: this.options.MapId,
          }
          this.map = new google.maps.Map(this.$refs.gmap, mapOptions)

          this.addMarkers()
          this.isLoaded = true
          this.$emit('loaded')
        } catch (error) {
          console.error(error)
        }
      },
    }
  }
</script>
